// What the schedule editor reads from the store (R-S6, decision 036): the event's slots, the queue of accepted talks, its partners,
// screens and rules, and the same program checks the server runs before publishing.

import { useMemo } from 'react';
import type { Id, RecordObj } from '@confdeck/core-shared';
import { recordTitle, useMeta, useSync, useTypesTick } from '@confdeck/core-web';
import { checkProgram, type CheckTalk } from '../src/program-check.ts';
import { readRules } from '../src/schedule-time.ts';

export function eventDays(start: unknown, end: unknown): string[] {
  if (typeof start !== 'string' || !start) return [];
  const out: string[] = [];
  const d = new Date(`${start}T00:00:00Z`);
  const last = new Date(`${typeof end === 'string' && end ? end : start}T00:00:00Z`);
  for (let i = 0; d <= last && i < 14; i++) { out.push(d.toISOString().slice(0, 10)); d.setUTCDate(d.getUTCDate() + 1); }
  return out;
}

export function useScheduleData(eventId: Id | null) {
  const client = useSync();
  const meta = useMeta();
  const T = (k: string) => meta.typeByKey(k);
  const slotT = T('slot'), talkT = T('talk'), partT = T('partnership'), screenT = T('screen');
  useTypesTick([slotT?.id ?? '', talkT?.id ?? '', partT?.id ?? '', screenT?.id ?? '']);
  const ver = (t: { id: Id } | undefined) => client.store.typeVersion.get(t?.id ?? '');
  const event = eventId ? client.store.record(eventId) : undefined;
  const linked = (r: RecordObj, typeId: Id, key: string) => { const f = meta.field(typeId, key); return f ? client.store.linked(r, f) : []; };
  const ofEvent = (typeId: Id | undefined) => {
    const out: RecordObj[] = [];
    if (typeId && eventId) for (const rid of client.store.recordIds(typeId)) { const r = client.store.record(rid); if (r && linked(r, typeId, 'event').includes(eventId)) out.push(r); }
    return out;
  };
  const termKey = (tid: unknown) => (typeof tid === 'string' ? meta.term(tid)?.key ?? null : null);
  /** A room or track value removed from its taxonomy: the slot is drawn apart and reported (audit 31.10, P2-10). */
  const termGone = (tid: unknown) => { if (typeof tid !== 'string' || !tid) return false; const t = meta.term(tid); return !t || !!t.archivedAt; };

  const { slots, talks, talksFree } = useMemo(() => {
    const slots = ofEvent(slotT?.id);
    const placed = new Set(slots.flatMap((r) => linked(r, slotT!.id, 'talk')));
    const talks = ofEvent(talkT?.id);
    const talksFree = talks.filter((t) => ['accepted', 'published'].includes(termKey(t.data.status) ?? '') && !placed.has(t.id));
    return { slots, talks, talksFree };
  }, [client.store, eventId, meta, ver(slotT), ver(talkT)]);
  const partners = useMemo(() => ofEvent(partT?.id), [client.store, eventId, meta, ver(partT)]);
  const screens = useMemo(() => ofEvent(screenT?.id).sort((a, b) => String(a.data.date ?? '').localeCompare(String(b.data.date ?? '')) || recordTitle(a, meta).localeCompare(recordTitle(b, meta), 'ru')), [client.store, eventId, meta, ver(screenT)]);
  const rules = readRules(event?.data.schedule_rules);

  // The same checks the server runs before publishing (Д-24), with the event's rules; conflicts are shown on the slots.
  const issues = useMemo(() => {
    if (!slotT || !talkT) return [];
    const toTalk = (t: RecordObj): CheckTalk => ({ id: t.id, title: recordTitle(t, meta), status: termKey(t.data.status), speakerIds: linked(t, talkT.id, 'speakers') });
    const map = new Map(talks.map((t) => [t.id, toTalk(t)]));
    return checkProgram(slots.map((x) => ({
      id: x.id, title: recordTitle(x, meta), date: String(x.data.date ?? ''), start: String(x.data.start ?? ''), duration: Number(x.data.duration ?? 0),
      room: (x.data.room as string) ?? null, track: (x.data.track as string) ?? null, kind: termKey(x.data.kind), talkId: linked(x, slotT.id, 'talk')[0] ?? null,
      proposal: !!x.data.proposal, partners: linked(x, slotT.id, 'partners').length, roomGone: termGone(x.data.room), trackGone: termGone(x.data.track),
    })), map, [...map.values()], rules, !!event?.data.schedule_rules);
  }, [slots, talks, meta, event?.data.schedule_rules]);

  return { client, meta, event, slots, talks, talksFree, partners, screens, rules, issues, linked, termKey, termGone, slotT, talkT, screenT };
}

export type ScheduleData = ReturnType<typeof useScheduleData>;
